import { AHIcon, AHSeparator, AHText, Box, ListItem } from "arkh";
import { FlatList, Pressable, StyleSheet } from "react-native";
import { graphql, useLazyLoadQuery, useMutation } from "react-relay";
import { ServiceAccountsScreenQuery } from "./__generated__/ServiceAccountsScreenQuery.graphql";
import { StoreAdminNavigationScreenProps } from "../StoreAdmin/StoreAdminNavigation";
import React, { Fragment, useCallback, useState } from "react";
import { TipArea } from "../../components";
import { LazyQueryNetworkBoundrary } from "../../components/mobile/QueryNetworkBoundrary";
import { FormattedMessage, useIntl } from "react-intl";
import { ServiceAccountsScreenDeleteMutation } from "./__generated__/ServiceAccountsScreenDeleteMutation.graphql";
import { MoreButton } from "@/src/common/MoreButton";
import { useIntlTokens } from "@/src/components/mobile/useIntlTokens";
import { ActionFooter } from "@/src/components/ActionFooter";

export const ServiceAccountsScreen = (
  props: StoreAdminNavigationScreenProps<"ServiceAccounts">
) => (
  <LazyQueryNetworkBoundrary newFetchKeyOnNavigation>
    {(fetchKey) => <InnerServiceAccountsScreen {...props} fetchKey={fetchKey} />}
  </LazyQueryNetworkBoundrary>
);

const InnerServiceAccountsScreen = (
  props: StoreAdminNavigationScreenProps<"ServiceAccounts"> & {
    fetchKey: string;
  }
) => {
  const [fetchKey, setFetchKey] = useState(0);

  const data = useLazyLoadQuery<ServiceAccountsScreenQuery>(
    graphql`
      query ServiceAccountsScreenQuery {
        store {
          serviceAccounts {
            id
            name
            authTokens {
              id
              createdAt {
                storeFormattedDate(style: SHORT_DATE)
              }
              lastUsedAt {
                storeFormattedDate(style: DEFAULT)
              }
              expiresAt {
                storeFormattedDate(style: DEFAULT)
                isoString
              }
            }
          }
        }
      }
    `,
    {},
    {
      fetchKey: `${props.fetchKey}:${fetchKey}`,
      fetchPolicy: "store-and-network",
    }
  );

  const [deleteMutation, deleteLoading] =
    useMutation<ServiceAccountsScreenDeleteMutation>(graphql`
      mutation ServiceAccountsScreenDeleteMutation($id: ID!) {
        store {
          serviceAccount(id: $id) {
            delete
          }
        }
      }
    `);

  const onDelete = useCallback(
    (id: string) => {
      deleteMutation({
        variables: { id },
        onCompleted: () => setFetchKey(fetchKey + 1),
      });
    },
    [deleteMutation, fetchKey, setFetchKey]
  );

  const intl = useIntl();
  const intl_tokens = useIntlTokens();

  return (
    <>
      <FlatList
        style={{ flex: 1 }}
        data={data.store.serviceAccounts}
        renderItem={({ item }) => (
          <ListItem
            key={item.id}
            right={
              <>
                {item.authTokens.length > 0 && (
                  <ServiceAccountStatus
                    expired={
                      item.authTokens[0].expiresAt
                        ? new Date(item.authTokens[0].expiresAt.isoString) <
                          new Date()
                        : false
                    }
                  />
                )}
                <MoreButton
                  items={[
                    {
                      label: intl_tokens.Delete,
                      icon: "trash",
                      onPress: () => onDelete(item.id),
                    },
                  ]}
                >
                  {(onOpen) => (
                    <Pressable
                      hitSlop={20}
                      onPress={onOpen}
                      style={({ pressed }) => ({ opacity: pressed ? 0.7 : 1 })}
                    >
                      <AHIcon spaceLeft="medium" type="more-vertical" />
                    </Pressable>
                  )}
                </MoreButton>
              </>
            }
            disabled={deleteLoading}
          >
            <Box gap="small">
              <ListItem.Title>{item.name}</ListItem.Title>
              {item.authTokens.map((authToken) => (
                <Fragment key={authToken.id}>
                  {authToken.expiresAt && (
                    <AHText>
                      Expires {authToken.expiresAt.storeFormattedDate ?? "-"}
                    </AHText>
                  )}
                  <AHText>
                    Created {authToken.createdAt.storeFormattedDate}
                  </AHText>
                  <AHText>
                    Last used {authToken.lastUsedAt?.storeFormattedDate ?? "-"}
                  </AHText>
                </Fragment>
              ))}
            </Box>
          </ListItem>
        )}
        ItemSeparatorComponent={AHSeparator}
        ListHeaderComponent={
          <Box space="medium">
            <TipArea>
              <AHText color="text-primary">
                <FormattedMessage
                  description="ServiceAccountsScreen - info"
                  defaultMessage="API Keys let external services send data into StoreSprint."
                />
              </AHText>
            </TipArea>
          </Box>
        }
      />
      <ActionFooter
        primary={
          <ActionFooter.Primary
            title={intl.formatMessage({
              description: "ServiceAccountsScreen - add account - label",
              defaultMessage: "Create Key",
            })}
            onPress={() =>
              props.navigation.navigateDeprecated("ServiceAccountCreate", {})
            }
            icon="add"
          />
        }
      />
    </>
  );
};

const ServiceAccountStatus = ({ expired }: { expired: boolean }) => {
  return (
    <Box cornerRadius="full" space="small" style={{ overflow: "hidden" }}>
      <Box
        style={[StyleSheet.absoluteFill, { opacity: 0.2 }]}
        color={expired ? "status-negative" : "status-positive"}
      />
      <AHText color={expired ? "status-negative" : "status-positive"}>
        {expired ? "Expired" : "Active"}
      </AHText>
    </Box>
  );
};
